import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOcupado } from '../lib/ocupado';

/**
 * Indicador de espera — arco suave girando, portado do `spinner.tsx` do isotec.
 *
 * SVG e não borda CSS: a borda gira um anel com as pontas CORTADAS em ângulo
 * reto, e no tamanho grande do overlay isso lê como um quadrado girando. O
 * arco com `strokeLinecap="round"` termina redondo, que é o que dá a leitura
 * de movimento suave.
 *
 * A trilha (o círculo a 20% de opacidade) fica visível de propósito: sem ela o
 * elemento parece sumir e reaparecer a cada volta.
 *
 * Presentacional puro — `animate-spin` do CSS, nenhum JS. `currentColor` nos
 * dois traços deixa a cor vir de quem usa; o padrão é a marca.
 */
export function Spinner({
  tamanho = 'md',
  className = '',
}: {
  tamanho?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const medida = { sm: 16, md: 24, lg: 40 }[tamanho];
  const traco = tamanho === 'lg' ? 3 : 2.5;

  return (
    <svg
      width={medida}
      height={medida}
      viewBox="0 0 24 24"
      fill="none"
      role="status"
      aria-label="Carregando"
      className={`text-marca-600 animate-spin ${className}`}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth={traco}
      />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth={traco}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Espera de tela inteira.
 *
 * Só aparece depois de 250 ms. Abaixo disso o spinner pisca e some — o que a
 * pessoa percebe como a tela "tremendo", não como carregamento. Requisição
 * rápida deve parecer instantânea, sem indicador nenhum.
 */
export function TelaCarregando({ mensagem }: { mensagem?: string }) {
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisivel(true), 250);
    return () => clearTimeout(t);
  }, []);

  if (!visivel) return null;

  return (
    <div className="animate-entrada flex min-h-64 flex-col items-center justify-center gap-3 py-16">
      <Spinner tamanho="lg" />
      <p className="text-tinta-500 text-sm">{mensagem ?? 'Carregando…'}</p>
    </div>
  );
}

/**
 * Bloco cinza pulsando, no lugar do conteúdo que ainda não chegou.
 *
 * Preferido ao spinner quando já se sabe o FORMATO do que vem (uma tabela, um
 * cartão): a tela não salta quando o dado chega, porque o espaço já estava
 * reservado.
 */
export function Esqueleto({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden className={`bg-tinta-100 animate-pulsar rounded-md ${className}`} />
  );
}

/** Esqueleto no formato de linhas de tabela. */
export function EsqueletoLinhas({
  linhas = 5,
  colunas = 4,
}: {
  linhas?: number;
  colunas?: number;
}) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: colunas }, (_, j) => (
            <Esqueleto
              key={j}
              className="h-4 flex-1"
              // A primeira coluna é sempre a mais larga nas nossas tabelas.
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Barra fina de progresso no topo, para troca de tela.
 *
 * Avança rápido até 90% e PARA — o resto só completa quando o carregamento
 * termina de verdade. Uma barra que chega a 100% sozinha e continua esperando
 * é pior que barra nenhuma: ela mente.
 */
export function BarraProgresso({ ativa }: { ativa: boolean }) {
  const [progresso, setProgresso] = useState(0);

  useEffect(() => {
    if (!ativa) {
      setProgresso((p) => (p > 0 ? 100 : 0));
      const t = setTimeout(() => setProgresso(0), 250);
      return () => clearTimeout(t);
    }

    setProgresso(12);
    const intervalo = setInterval(() => {
      setProgresso((p) => (p >= 90 ? p : p + (90 - p) * 0.18));
    }, 180);
    return () => clearInterval(intervalo);
  }, [ativa]);

  if (progresso === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5" aria-hidden>
      <div
        className="bg-marca-600 h-full transition-[width,opacity] duration-200"
        style={{ width: `${progresso}%`, opacity: progresso === 100 ? 0 : 1 }}
      />
    </div>
  );
}

/**
 * Indicador GLOBAL — cartão central com o spinner, sobre a tela inteira.
 *
 * Portado do `global-loading.tsx` do isotec, com a lição que ele documenta e
 * que é a parte não óbvia: **revalidação NÃO acende nada aqui.**
 *
 * O filtro é `state.data === undefined` — só consulta que ainda não tem
 * resposta nenhuma. Somar `useIsFetching()` inteiro é o erro fácil: aí toda
 * revalidação em segundo plano (voltar para uma tela cujo dado passou do
 * `staleTime`, clicar em Atualizar, trocar de filtro) joga um overlay por cima
 * de uma tabela JÁ preenchida e correta. A tela carrega normal e mesmo assim
 * parece presa atrás do spinner — o oposto do que o indicador existe para
 * fazer.
 *
 * Dois modos, e a diferença entre eles separa "aviso" de "tela travada":
 *
 *  - **bloqueante** (gravação em curso): escurece e CAPTURA cliques, porque a
 *    operação muda dado e não pode receber um segundo clique no meio;
 *  - **leve** (primeira carga de uma consulta): mesmo cartão, mas
 *    `pointer-events-none` e fundo mais fraco — é aviso, não bloqueio.
 *
 * As outras camadas continuam existindo e são preferíveis quando cabem: o
 * esqueleto da `Tabela` (que reserva o espaço e não faz a tela saltar) e a
 * `BarraProgresso`. Este aqui é a rede para o que elas não cobrem.
 */
export function CarregandoGlobal(): JSX.Element | null {
  const gravando = useIsMutating();
  const primeiraCarga = useIsFetching({
    predicate: (q) => q.state.data === undefined,
  });

  /*
    A terceira fonte, e a que faltava: ação que o usuário PEDIU.

    Revalidação continua silenciosa — é a regra acima, e ela está certa para
    o que acontece sozinho (o dado ficou velho, a janela recebeu foco). Mas
    clicar em Consultar ou em Atualizar é diferente em espécie: a pessoa
    pediu, está olhando para a tela, e sem indicador — com a rede lenta — ela
    clica de novo ou conclui que travou.

    Quem marca é o `useOcupado` (ver `lib/ocupado.ts`), não o React Query:
    ele não tem como saber quem disparou a busca.
  */
  const pedido = useOcupado((e) => e.ocupado);
  const rotulo = useOcupado((e) => e.rotulo);

  const bloqueante = gravando > 0;
  const ativo = bloqueante || pedido > 0 || primeiraCarga > 0;

  /*
    Atrasos diferentes, e não um só.

    Gravação costuma ser instantânea: 250 ms evita o flash de um spinner que
    aparece e some, que a pessoa lê como a tela "tremendo". Já a primeira carga
    precisa responder rápido ao clique — se demorar, volta a parecer que o
    clique não pegou, que é justamente a queixa que este componente resolve.
  */
  const atraso = bloqueante ? 250 : 120;
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    if (!ativo) {
      setVisivel(false);
      return;
    }
    const t = setTimeout(() => setVisivel(true), atraso);
    return () => clearTimeout(t);
  }, [ativo, atraso]);

  if (!visivel) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed inset-0 z-[120] flex items-center justify-center ${
        bloqueante ? 'bg-fundo/45 backdrop-blur-[2px]' : 'bg-fundo/20 pointer-events-none'
      }`}
    >
      <div className="border-tinta-100 bg-superficie flex flex-col items-center gap-3 rounded-xl border px-8 py-6 shadow-lg">
        <Spinner tamanho="lg" />
        <span className="text-tinta-500 text-xs font-medium">
          {rotulo ?? (bloqueante ? 'Salvando…' : 'Carregando…')}
        </span>
      </div>
    </div>
  );
}
